import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Switch from "../common/Switch";
import { updateAccessContactPermissions } from "../../services/eventDetailService";
import { color } from "../../styles/theme";

const PERMISSION_FIELD_DEFS = [
  { key: "canManageQR", apiKey: "CanManageQR" },
  { key: "canManageChat", apiKey: "CanManageChat" },
  { key: "canKickOrBlockUsers", apiKey: "CanKickOrBlockUsers" },
];

const EditAccessContactPermissionsModal = ({ accessContact, name, onClose, onUpdated }) => {
  const { t } = useTranslation(["eventDetails", "common"]);
  const permissionFields = useMemo(
    () => PERMISSION_FIELD_DEFS.map((field) => ({
      ...field,
      label: t(`editAccessContactPermissionsModal.fields.${field.key}`),
    })),
    [t]
  );
  const initial = useMemo(
    () => permissionFields.reduce((values, field) => ({
      ...values,
      [field.key]: accessContact?.[field.apiKey] !== false,
    }), {}),
    [accessContact, permissionFields]
  );
  const [values, setValues] = useState(initial);
  const [saving, setSaving] = useState(false);

  const hasChanges = permissionFields.some((field) => values[field.key] !== initial[field.key]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await updateAccessContactPermissions(accessContact.Id, values);
      toast.success(response.data?.Message || t("editAccessContactPermissionsModal.updateSuccess"));
      onUpdated();
    } catch (error) {
      toast.error(error.response?.data?.Message || t("editAccessContactPermissionsModal.updateError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t("editAccessContactPermissionsModal.title")}
      subtitle={name}
      onClose={onClose}
      busy={saving}
      maxWidth={400}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>{t("common:actions.cancel")}</Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? t("editAccessContactPermissionsModal.saving") : t("common:actions.save")}
          </Button>
        </>
      }
    >
      <div style={styles.list}>
        {permissionFields.map((field, index) => (
          <div
            key={field.key}
            style={{
              ...styles.row,
              ...(index === permissionFields.length - 1 ? { borderBottom: "none" } : {}),
            }}
          >
            <span style={styles.rowLabel}>{field.label}</span>
            <Switch
              checked={values[field.key]}
              onChange={() => setValues((current) => ({ ...current, [field.key]: !current[field.key] }))}
              disabled={saving}
            />
          </div>
        ))}
      </div>
    </Modal>
  );
};

const styles = {
  list: { display: "flex", flexDirection: "column" },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    padding: "12px 2px",
    borderBottom: `1px solid ${color.gray100}`,
  },
  rowLabel: { fontSize: 13.5, color: color.gray800, fontWeight: 500 },
};

export default EditAccessContactPermissionsModal;
